import React, { useRef } from 'react'
import type { NodeApi, NodeRendererProps, TreeApi } from 'react-arborist'
import { Tree } from 'react-arborist'
import { useMeasure } from 'react-use'
import { HorizontalStack, Icon } from '@ztechapps/webui.util'
import styles from './data-tree.module.scss'
import type { TreeData } from './constants'
import { useDataTreeContext } from './index'

const INDENT_STEP = 25

export const Layout = () => {
  const { data, treeOptions } = useDataTreeContext()
  const treeRef = useRef<TreeApi<TreeData>>(undefined)
  const [refContainer, { height }] = useMeasure()

  const { width, ...restTreeOptions } = treeOptions ?? {}

  return (
    <div ref={refContainer} className={`${styles.treeContainer} flex-fill `}>
      <Tree
        rowHeight={30}
        paddingTop={20}
        width={width ?? '20vw'}
        selectionFollowsFocus={true}
        indent={INDENT_STEP}
        openByDefault={false}
        ref={treeRef}
        height={height}
        data={data}
        rowClassName={styles.row}
        className={styles.tree}
        overscanCount={8}
        {...(restTreeOptions as any)}
      >
        {Node}
      </Tree>
    </div>
  )
}

function FolderArrow({ node }: { node: NodeApi<TreeData> }) {
  return (
    <span className={styles.arrow}>
      {node.isInternal ? node.isOpen ? <Icon name="chevron-down" /> : <Icon name="chevron-right" /> : null}
    </span>
  )
}

function Node({ node, style, dragHandle }: NodeRendererProps<TreeData>) {
  const { childIcon, parentIcon } = useDataTreeContext()
  const normalizedChildIcon = typeof childIcon === 'string' ? childIcon : childIcon(node.data)
  const normalizedParentIcon = typeof parentIcon === 'string' ? parentIcon : parentIcon(node.data)

  const icon = node.isInternal ? normalizedParentIcon : normalizedChildIcon
  const indentSize = Number.parseFloat(`${style.paddingLeft || 0}`)

  const obj = node.state
  const nodeStyles = Object.keys(obj)
    .filter((key) => obj[key])
    .join(' ')

  return (
    <div
      ref={dragHandle}
      style={style}
      className={`${styles.node} ${nodeStyles}`}
      onClick={() => node.isInternal && node.toggle()}
    >
      <HorizontalStack>
        <div className={styles.indentLines}>
          {Array.from({ length: indentSize / INDENT_STEP }, (_, index) => (
            <div key={index} />
          ))}
        </div>
        <HorizontalStack gap={1}>
          <FolderArrow node={node} />
          <Icon name={icon} />
          <span className={styles.text}>{node.data.name}</span>
        </HorizontalStack>
      </HorizontalStack>
    </div>
  )
}
